import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Plus, X, Award, Printer, FileBadge } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const TYPES = [
  { k: "birth", icon: Award },
  { k: "death", icon: Award },
  { k: "marriage", icon: Award },
  { k: "residence", icon: Award },
  { k: "income", icon: Award },
  { k: "bpl", icon: Award },
  { k: "widow", icon: Award },
  { k: "construction_permission", icon: Award },
  { k: "toilet", icon: Award },
  { k: "boundary_chatusima", icon: Award },
  { k: "govt_loan", icon: Award },
];

const FIELDS = {
  birth: [
    ["child_name", "childName", true],
    ["dob", "dob", true, "date"],
    ["gender", "gender", true, "select", ["male", "female", "other"]],
    ["place_of_birth", "placeOfBirth", true],
    ["father_name", "fatherName", true],
    ["mother_name", "motherName", true],
    ["address", "address", true],
  ],
  death: [
    ["deceased_name", "deceasedName", true],
    ["dod", "dod", true, "date"],
    ["age", "age", true, "number"],
    ["gender", "gender", true, "select", ["male", "female", "other"]],
    ["place_of_death", "placeOfDeath", true],
    ["father_name", "fatherName"],
    ["address", "address", true],
  ],
  marriage: [
    ["husband_name", "husbandName", true],
    ["wife_name", "wifeName", true],
    ["marriage_date", "marriageDate", true, "date"],
    ["place_of_marriage", "placeOfMarriage", true],
    ["address", "address", true],
  ],
  residence: [
    ["applicant_name", "Applicant Name", true],
    ["father_name", "fatherName", true],
    ["address", "address", true],
    ["residing_since", "Residing Since", true, "date"],
    ["purpose", "Purpose"],
  ],
  income: [
    ["applicant_name", "Applicant Name", true],
    ["father_name", "fatherName", true],
    ["address", "address", true],
    ["annual_income", "Annual Income (₹)", true, "number"],
    ["source", "Source of Income", true],
  ],
  bpl: [
    ["applicant_name", "Applicant Name", true],
    ["family_id", "Family ID / PPP", true],
    ["address", "address", true],
    ["category", "Category (SC/ST/OBC/Gen)", true],
    ["family_size", "Family Size", true, "number"],
  ],
  widow: [
    ["applicant_name", "Applicant Name", true],
    ["husband_name", "husbandName", true],
    ["husband_dod", "Husband's DoD", true, "date"],
    ["address", "address", true],
  ],
  construction_permission: [
    ["applicant_name", "Applicant Name", true],
    ["plot_no", "Plot No.", true],
    ["address", "address", true],
    ["built_area_sqft", "Built Area (sqft)", true, "number"],
    ["floors", "Floors", true, "number"],
    ["purpose", "Purpose (residential/commercial)", true],
  ],
  toilet: [
    ["applicant_name", "Applicant Name", true],
    ["address", "address", true],
    ["construction_date", "Construction Date", true, "date"],
    ["scheme", "Scheme (SBM/own)"],
  ],
  boundary_chatusima: [
    ["plot_owner", "Plot Owner", true],
    ["khasra_no", "Khasra No.", true],
    ["address", "Plot Address", true],
    ["north_boundary", "North Boundary", true],
    ["south_boundary", "South Boundary", true],
    ["east_boundary", "East Boundary", true],
    ["west_boundary", "West Boundary", true],
  ],
  govt_loan: [
    ["applicant_name", "Applicant Name", true],
    ["address", "address", true],
    ["loan_purpose", "Loan Purpose", true],
    ["loan_amount", "Loan Amount (₹)", true, "number"],
    ["scheme", "Scheme"],
  ],
};

export default function Certificates() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("birth");
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);

  const load = () => {
    const params = { type: tab };
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    api.get("/certificates", { params }).then((r) => setList(r.data));
  };

  useEffect(() => { load(); }, [tab, user]);

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{t("certificates")}</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900">{t("certificates")}</h1>
          </div>
          <button
            data-testid="issue-cert-btn"
            onClick={() => setShow(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}
          >
            <Plus className="w-4 h-4" /> {t("issueCertificate")}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 mb-4 overflow-x-auto">
          {TYPES.map((tp) => (
            <button
              key={tp.k}
              onClick={() => setTab(tp.k)}
              data-testid={`tab-${tp.k}`}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === tp.k ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t(tp.k)}
            </button>
          ))}
        </div>

        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="cert-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">{t("certificateNo")}</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">{t("issueDate")}</th>
                <th className="text-left px-4 py-3">{t("status")}</th>
                <th className="text-right px-4 py-3">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const name = c.data?.child_name || c.data?.deceased_name || `${c.data?.husband_name || ""} & ${c.data?.wife_name || ""}`;
                const date = c.data?.dob || c.data?.dod || c.data?.marriage_date || "";
                const badgeCls =
                  c.approval_status === "approved" ? "bg-emerald-50 text-emerald-700" :
                  c.approval_status === "rejected" ? "bg-red-50 text-red-700" :
                  "bg-amber-50 text-amber-700";
                return (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">{c.certificate_no}</td>
                    <td className="px-4 py-3 font-medium">{name}</td>
                    <td className="px-4 py-3">{date}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{new Date(c.issued_at).toLocaleDateString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded font-semibold ${badgeCls}`}>
                        {c.approval_status || c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/certificates/print/${c.id}`} data-testid={`cert-print-${c.certificate_no}`} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md border border-[#0A3161] text-[#0A3161]">
                        <Printer className="w-3 h-3" /> {t("print")}
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-slate-400 text-sm">No {tab} certificates</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {show && (
        <IssueModal
          tab={tab}
          panchayat_id={user?.panchayat_id}
          onClose={() => setShow(false)}
          onCreated={() => { setShow(false); load(); }}
        />
      )}
    </OfficerLayout>
  );
}

function IssueModal({ tab, panchayat_id, onClose, onCreated }) {
  const { t } = useLang();
  const [panchayats, setPanchayats] = useState([]);
  const [pid, setPid] = useState(panchayat_id || "");
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/panchayats").then((r) => {
      setPanchayats(r.data);
      if (!pid) setPid(r.data[0]?.id || "");
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/certificates", { type: tab, panchayat_id: pid, data });
      toast.success("Certificate issued");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="cert-modal">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">{t("issueCertificate")}</div>
            <h2 className="font-heading text-xl font-bold mt-1">{t(tab)} Certificate</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{t("panchayat")}</label>
            <select required value={pid} onChange={(e) => setPid(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm">
              {panchayats.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start justify-between gap-3">
            <div className="text-xs text-amber-900">
              <div className="font-semibold">DEMO Integration</div>
              <div className="text-amber-800">Auto-fill citizen data from DigiLocker (mocked).</div>
            </div>
            <button type="button" data-testid="digilocker-btn"
              onClick={async () => {
                const mobile = prompt("Enter registered mobile (DEMO — try 9876543210):");
                if (!mobile) return;
                try {
                  const r = await api.post("/integrations/digilocker/pull", { mobile });
                  if (r.data.documents.length === 0) {
                    toast.error("No DigiLocker docs found for this mobile (DEMO).");
                    return;
                  }
                  const list = r.data.documents.map(d => `• ${d.type} (${d.issued_by}, ${d.year})`).join("\n");
                  toast.success(`Found ${r.data.documents.length} document(s)`);
                  alert(`DigiLocker Docs (DEMO):\n\n${list}`);
                } catch (e) { toast.error("Failed"); }
              }}
              className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-md border border-amber-700 text-amber-900 hover:bg-amber-100 whitespace-nowrap">
              {t("digiLocker")}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS[tab].map((f) => {
              const [key, lblKey, required, type = "text", options] = f;
              return (
                <div key={key}>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
                    {t(lblKey)}{required && <span className="text-[#E87722] ml-0.5">*</span>}
                  </label>
                  {type === "select" ? (
                    <select required={required} value={data[key] || ""} onChange={(e) => setData({ ...data, [key]: e.target.value })}
                      className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm" data-testid={`cf-${key}`}>
                      <option value="">— Select —</option>
                      {options.map((o) => <option key={o} value={o}>{t(o)}</option>)}
                    </select>
                  ) : (
                    <input type={type} required={required} value={data[key] || ""} onChange={(e) => setData({ ...data, [key]: e.target.value })}
                      className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm outline-none focus:border-[#0A3161]" data-testid={`cf-${key}`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-300">{t("cancel")}</button>
            <button type="submit" disabled={busy} data-testid="cert-submit" className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
              {busy ? "…" : t("issueCertificate")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
